import { IRuleModule } from "../interfaces/IRuleModule";
import { RuleItem } from "../interfaces/RuleItem";
import { RuleModuleType } from "../constants/constants";
import { RuleItemConfig } from "../interfaces/RuleItemConfig";
import { RankData } from "../interfaces/RankData";
import { BonusSourceDetails } from "../interfaces/BonusSourceDetails";
import { StatDefinition } from "../interfaces/StatDefinition";
import { AddAlert } from "./AlertCenter";
import { ActionResult } from "../interfaces/ActionResult";
import { AlertLevel } from "../types/AlertTypes";
import { LevelUpOptions } from "../interfaces/LevelUpOptions";

const defaultConfig: RuleItemConfig = {
  min: 0,
  max: 20,
  default: 0,
};

export class RuleModule implements IRuleModule {
  moduleName: string = "Module";
  ruleItemName: string = "Item";
  statsName: string = "Stats";
  statsKey: string = "statsKey";
  key: string = "modkey";
  moduleType: RuleModuleType = RuleModuleType.ItemOnly;
  list: RuleItem[] = [];
  config: RuleItemConfig = defaultConfig;
  levelUpOptions?: LevelUpOptions[];
  index: { [key: string]: number } = {};
  updateCallback: () => void;
  addKeyCallback: (key: string, triggerUI: boolean) => ActionResult;
  checkKeyDuplicateCallback: (key: string, triggerUI: boolean) => ActionResult;
  removeKeyCallback: (key: string) => ActionResult;
  addModuleAlert: (alert: ActionResult) => ActionResult;
  addItemAlert: AddAlert;

  constructor(
    iRuleModule: IRuleModule,
    updateCallback: () => void,
    addKeyCallback: (key: string, triggerUI: boolean) => ActionResult,
    checkKeyDuplicateCallback: (
      key: string,
      triggerUI: boolean
    ) => ActionResult,
    removeKeyCallback: (key: string) => ActionResult,
    addModuleAlert: AddAlert,
    addItemAlert: AddAlert
  ) {
    this.addModuleAlert = (actionResult: ActionResult) =>
      addModuleAlert(iRuleModule.key, actionResult);
    this.addItemAlert = addItemAlert;
    this.updateCallback = updateCallback;
    this.addKeyCallback = addKeyCallback;
    this.checkKeyDuplicateCallback = checkKeyDuplicateCallback;
    this.removeKeyCallback = removeKeyCallback;
    const { list, ...IRuleMOduleNoItems } = iRuleModule;
    Object.assign(this, IRuleMOduleNoItems);
    this.statsName = this.config.selectOptions?.name || this.statsName;
    this.statsKey = this.config.selectOptions?.key || this.statsKey;
    list.forEach((item: RuleItem, i: number) => {
      if (this.addItem(item, false).success) {
        this.index[item.key] = i;
      }
    });
    this.getStatDefinitions().ruleItems.forEach((stat) => {
      this.addKeyCallback(stat.key, false);
    });
  }

  public getKeys = (): string[] => {
    return Object.keys(this.index);
  };

  public getRuleItem = (key: string): RuleItem => {
    if (!(key in this.index)) {
      throw Error(
        `Could not find '${key}' in ${this.moduleName}. Potential race condition if key was added to ephemeral list before the source of truth here.`
      );
    }
    return this.list[this.index[key]];
  };

  public addItem = (
    item: RuleItem,
    triggerUI: boolean = true
  ): ActionResult => {
    item.key = item.key.trim();
    if (item.name.length < 1) {
      return this.addModuleAlert({
        success: false,
        message: "Name can not be empty.",
        level: AlertLevel._2_RULE,
        triggerUI,
      });
    }
    item.key = item.key.toLowerCase().trim().replace(" ", "");
    if (item.key.length < 1) {
      return this.addModuleAlert({
        success: false,
        message: "Key can not be empty.",
        level: AlertLevel._2_RULE,
        triggerUI,
      });
    }
    const dupResult: ActionResult = this.checkKeyDuplicateCallback(
      item.key,
      triggerUI
    );
    if (!dupResult.success) {
      console.log("DUPLICATE", item.key);
      return dupResult;
    }
    if (item.key in this.index) {
      return this.addModuleAlert({
        success: false,
        message: `${this.moduleName} key '${item.key}' already exists.`,
        level: AlertLevel._2_RULE,
        triggerUI,
      });
    }
    this.list.push(item);
    this.rebuildIndex();
    const addKeyResult: ActionResult = this.addKeyCallback(item.key, triggerUI);
    if (!addKeyResult.success) {
      return addKeyResult;
    }
    return this.addModuleAlert({
      success: true,
      message: `Added '${item.key}' to ${this.moduleName}.`,
      level: AlertLevel._4_LOG,
      triggerUI,
    });
  };

  public updateItemDefinition = (item: RuleItem): ActionResult => {
    if (!(item.key in this.index)) {
      alert(`Failed to update ${item.name} - ${item.key}`);
      return this.addModuleAlert({
        success: false,
        message: `${this.moduleName} doesn't have key '${item.key}'.`,
        level: AlertLevel._0_CRITICAL,
      });
    }
    this.list[this.index[item.key]] = item;
    this.updateCallback();
    return {
      success: true,
      message: `Updated '${item.name}'.`,
      level: AlertLevel._4_LOG,
    };
  };

  public deleteItem = (key: string): ActionResult => {
    if (!(key in this.index)) {
      return this.addModuleAlert({
        success: false,
        message: `No Attribute key '${key}'.`,
        level: AlertLevel._1_ERROR,
      });
    }
    const index = this.index[key];
    this.list.splice(index, 1);
    this.rebuildIndex();
    return this.addModuleAlert(this.removeKeyCallback(key));
  };

  public availableRuleItems = (rankData: {
    [key: string]: RankData;
  }): RuleItem[] => {
    let currentItemCount = 0;
    const availableRuleItems: RuleItem[] = [];
    this.list.forEach((item: RuleItem, i: number) => {
      if (!(item.key in rankData)) {
        availableRuleItems.push(item);
      } else {
        currentItemCount += 1;
      }
    });
    return this.showAvailableItems(currentItemCount) ? availableRuleItems : [];
  };

  public showAvailableItems = (currentItemCount: number): boolean => {
    if (this.config.selectOptions?.maxItemCount) {
      return currentItemCount < this.config.selectOptions.maxItemCount;
    }
    return true;
  };

  public getStatDefinitions = (): {
    ruleItems: RuleItem[];
    statDefinitions: StatDefinition[];
  } => {
    const ruleItems: RuleItem[] = [];
    const statDefinitions: StatDefinition[] = [];
    if (
      this.config.selectOptions &&
      this.config.selectOptions.statDefinitions
    ) {
      for (let key in this.config.selectOptions.statDefinitions) {
        const item: StatDefinition = this.config.selectOptions.statDefinitions[
          key
        ];
        ruleItems.push({ key, name: item.name, details: {} });
        statDefinitions.push(item);
      }
    }
    return { ruleItems, statDefinitions };
  };

  public rebuildIndex = (): void => {
    this.index = {};
    for (var i = 0; i < this.list.length; i++) {
      this.index[this.list[i].key] = i;
    }
  };

  public emptyBonusSourceDetailsForItem = (key: string): BonusSourceDetails => {
    const ruleItem: RuleItem = this.getRuleItem(key);
    return {
      rank: 0,
      type: this.moduleType,
      bonusesFromSources: [],
      targetedBonuses: [],
      progressionBonuses: [],
      rankIncreases: [],
      actualBonus: {
        sourceName: ruleItem.name,
        sourceKey: ruleItem.key,
        current: 0,
        bonusDetailType: "bonus-unknown",
      },
    };
  };

  public ruleItemNameNormalized = (): string => {
    return this.ruleItemName.replace(" ", "");
  };

  public dataTest = (): string => {
    return `${this.moduleName.toLowerCase().replace(" ", "-")}-${this.key}`;
  };
}
